import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import "./index.css";

/* ─── Configuration Basics ────────────────────────────────────────────────── */
const EFFECTIVE_DATE = "June 1, 2026";
const LAST_UPDATED   = "June 1, 2026";

/* ─── Assets ─────────────────────────────────────────────────────────────── */
const LOGO_SRC = "/logo192.png";

interface PolicySection {
  num: string;
  title: string;
  content: React.ReactNode;
}

/* ─── Policy Data Definitions with Updated Infrastructure Disclosures ─────── */
const SECTIONS: PolicySection[] = [
  {
    num: "01",
    title: "Overview",
    content: (
      <>
        <p className="pp-p">
          Mosaic Ventures LLC ("Mosaic Ventures," "we," "us," or "our") operates the platform layer located at
          mosaicventures.co (the "Site"). This Privacy Policy transparently defines how we collect, process,
          and secure your information parameters when interacting with our operational framework or submitting an inquiry.
        </p>
        <p className="pp-p">
          By utilizing this platform, you affirm your understanding of the baseline practices detailed herein.
        </p>
      </>
    ),
  },
  {
    num: "02",
    title: "Information Ingestion Architecture",
    content: (
      <>
        <p className="pp-p">
          We minimize information data points to strictly operational vectors, categorizing incoming traffic through voluntary transmission and essential infrastructure metrics.
        </p>
        <p className="pp-p"><strong style={{ color: "var(--ink)", fontWeight: 500 }}>Explicit Parameters Provided By You:</strong></p>
        <ul className="pp-list">
          <li>Identity vectors (Full Name, Professional/Institutional affiliation).</li>
          <li>Communications routing parameters (Direct Email Address).</li>
          <li>Intent indicators (Partnership tier designation and any text payloads provided within your inquiry formulation).</li>
        </ul>
        <p className="pp-p" style={{ marginTop: 14 }}><strong style={{ color: "var(--ink)", fontWeight: 500 }}>Stateless Diagnostic Parameters Collected Automatically:</strong></p>
        <ul className="pp-list">
          <li>Standard browser configurations, device variations, and system properties.</li>
          <li>IP addresses mapped to generalized high-level regional nodes (city and state/country tier parameters).</li>
          <li>Stateless structural interaction metrics (timestamp deltas and targeted system navigation flow indicators).</li>
        </ul>
      </>
    ),
  },
  {
    num: "03",
    title: "Operational Utilization Framework",
    content: (
      <>
        <p className="pp-p">We process information parameters strictly for defined institutional objectives:</p>
        <ul className="pp-list">
          <li>To evaluate, route, and execute inbound strategic partnership alignments.</li>
          <li>To coordinate operational updates regarding our active deployment pipelines (e.g., the Atlanta Distributed Infrastructure Initiative).</li>
          <li>To analyze infrastructural traffic densities to protect, stabilize, and tune the Site layout.</li>
          <li>To intercept, isolate, and mitigate coordinated security anomalies or unauthorized programmatic execution routines.</li>
        </ul>
        <p className="pp-p">
          We maintain absolute alignment with user trust: we do not trade, lease, or monetize identity packets to third-party ad brokers or use tracking telemetry for retargeting mechanisms.
        </p>
      </>
    ),
  },
  {
    num: "04",
    title: "Downstream Subprocessors & Infrastructure Disclosures",
    content: (
      <>
        <p className="pp-p">
          We utilize highly secure third-party deployment vendors to route operational signals safely. Data packets are distributed exclusively within the constraints detailed below:
        </p>
        <ul className="pp-list">
          <li>
            <strong style={{ color: "var(--ink)", fontWeight: 500 }}>Third-Party Form Ingestion Layer:</strong>{" "}
            Partner intake frameworks are transmitted securely using TLS transport protocols to our designated delivery processor, <strong style={{ color: "var(--ink)" }}>Web3Forms</strong>. Web3Forms acts strictly as a data subprocessor, executing automated transmission routines to deliver your inquiry securely to our management team without storing payloads within persistent open indexes.
          </li>
          <li>
            <strong style={{ color: "var(--ink)", fontWeight: 500 }}>Regulatory Compliance & Mitigation:</strong>{" "}
            We reserve the right to pass operational data logs if mandated by binding judicial orders, governmental audit structures, or to protect physical network integrity.
          </li>
        </ul>
      </>
    ),
  },
  {
    num: "05",
    title: "Infrastructural Protection & Anti-Bot Detection Telemetry",
    content: (
      <>
        <p className="pp-p">
          To bypass tracking frameworks, this platform avoids user tracking meshes and tracking cookies.
        </p>
        <p className="pp-p">
          However, to preserve network bandwidth and protect our intake layer against programmatic automated form-fill networks and bot frameworks, our form components evaluate stateless transactional markers. These defenses evaluate hidden form vectors (honeypot fields) and calculate interaction timing deltas (measuring speed velocity tolerances between form visibility and click submission). 
        </p>
        <p className="pp-p">
          This behavior evaluation occurs purely client-side or at runtime during transmission verification; the volatile structural parameters generated by these checks are never saved permanently to disk or integrated into long-term user profiles.
        </p>
      </>
    ),
  },
  {
    num: "06",
    title: "Data Retention Limits",
    content: (
      <p className="pp-p">
        We store personal information data files only for the timeframe necessary to safely conclude active integration assessments, maintain secure institutional histories, or guarantee regulatory compliance metrics. You can execute structural audits or initiate permanent removal requests of your specific information logs by transmitting a verified parameter request straight to our compliance endpoint at{" "}
        <a className="pp-link" href="mailto:contact@mosaicventures.co">contact@mosaicventures.co</a>.
      </p>
    ),
  },
  {
    num: "07",
    title: "Global Compliance Assertion Rights",
    content: (
      <>
        <p className="pp-p">
          Depending on your regional legal standard, you can leverage explicit administrative asset safeguards over your identity parameters. These configurations include:
        </p>
        <ul className="pp-list">
          <li>The right to view comprehensive records of saved personal variables.</li>
          <li>The right to fix corrupted or inaccurate tracking criteria.</li>
          <li>The right to demand complete deletion of active operational records.</li>
          <li>The right to revoke operational consent guidelines completely.</li>
        </ul>
        <p className="pp-p">
          To file a legal validation request or verify your profile parameters, reach out to our team at{" "}
          <a className="pp-link" href="mailto:contact@mosaicventures.co">contact@mosaicventures.co</a>. We execute compliant confirmations within all mandated legal processing periods.
        </p>
      </>
    ),
  },
];

/* ─── Component Componentry ──────────────────────────────────────────────── */
export default function PrivacyPolicy(): React.ReactElement {
  // Ensure the UI shifts back to top coordinate when user routes to this view
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="pp-wrap">
      {/* ── Dynamic Head Metadata Isolation ── */}
      <Helmet>
        <title>Privacy Policy | Mosaic Ventures</title>
        <meta name="description" content="Review the official Privacy Policy of Mosaic Ventures LLC. Learn how we handle enterprise structural metrics and visitor inquiries securely." />
        {/* ✅ FIXED: This explicitly notifies search engine indexes that this is an independent page path */}
        <link rel="canonical" href="https://mosaicventures.co/privacy" />
        
        <meta property="og:title" content="Privacy Policy | Mosaic Ventures" />
        <meta property="og:url" content="https://mosaicventures.co/privacy" />
        <meta property="og:description" content="Review how data and corporate coordination interactions are kept confidential by Mosaic Ventures." />
      </Helmet>

      <a href="#pp-main" className="skip-link">Skip to legal content</a>

      {/* ── Navbar ── */}
      <header className="pp-nav" role="banner">
        {/* ✅ FIXED: Uses standard Link context instead of a layout refresh mechanism */}
        <Link to="/" className="pp-brand">
          <img src={LOGO_SRC} alt="" width={30} height={30} />
          Mosaic Ventures
        </Link>
        <Link to="/" className="pp-back" aria-label="Return to landing layout">
          <svg viewBox="0 0 24 24"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
          Back Home
        </Link>
      </header>

      {/* ── Page Content ── */}
      <main className="pp-page" id="pp-content">
        <div className="pp-inner">

          {/* ── Header ── */}
          <p className="pp-eyebrow">Legal</p>
          <h1 className="pp-title">Privacy <em>Policy</em></h1>
          <p className="pp-meta">
            Effective date: {EFFECTIVE_DATE} &nbsp;·&nbsp; Last updated: {LAST_UPDATED}
          </p>

          {/* ── Dynamically Render Structured Sections ── */}
          <div className="pp-body">
            {SECTIONS.map((sec) => (
              <section className="pp-section" key={sec.num} aria-labelledby={`sec-${sec.num}`}>
                <span className="pp-section-num">{sec.num}</span>
                <h2 className="pp-section-title" id={`sec-${sec.num}`}>{sec.title}</h2>
                {sec.content}
              </section>
            ))}

            <hr className="pp-divider" aria-hidden="true" />

            {/* ── Contact Context Block ── */}
            <div className="pp-contact" role="complementary" aria-label="Contact information">
              <p className="pp-contact-label">Transparency Operations</p>
              <h2 className="pp-contact-h">Contact Us</h2>
              <p className="pp-contact-p">
                If you have structural questions concerning this data policy or our network safety integrations,
                reach our compliance coordinate at{" "}
                <a className="pp-link" href="mailto:contact@mosaicventures.co">
                  contact@mosaicventures.co
                </a>{" "}
                or submit post to:<br /><br />
                Mosaic Ventures LLC<br />
                8735 Dunwoody Place, STE N<br />
                Atlanta, GA 30350
              </p>
            </div>

            {/* ── Footer Link Matrix ── */}
            <footer className="pp-footer">
              <p className="pp-footer-copy">
                © {new Date().getFullYear()} Mosaic Ventures LLC. All rights reserved.
              </p>
              <nav className="pp-footer-nav" aria-label="Footer navigation">
                <Link to="/">Home</Link>
                <a href="/#mission">Mission</a>
                <a href="/#initiatives">Initiatives</a>
                <a href="/#conditions">Intelligence</a>
                <a href="/#atlanta">Why Atlanta</a>
                <a href="/#thesis">Thesis</a>
                <a href="/#contact">Contact</a>
              </nav>
            </footer>
          </div>
        </div>
      </main>
    </div>
  );
}